import React, { useMemo, useState } from 'react';
import { Button, ButtonGroup, Divider, Flex, Paragraph, TextButton } from '@zillow/constellation';
import { format } from 'date-fns';
import { RecurringFrequency } from '@zg-rentals/ts-z-calendar-client';

import RecuringAvailabilityConfirmationModal from '../RecuringAvailabilityConfirmationModal';
import { type AvailabilityEvent, CALENDAR_EVENT_DATE_DISPLAY_FORMAT_STRING } from '../../../../constants/TourCalendar';
import {
  getDailyRecurrenceString,
  getTourEventTimeRange,
  getWeeklyRecurrenceString,
} from '../../../../utils/formatTime';

const getRecurringDaysSection = (event: AvailabilityEvent) => {
  const isDailyRecurringEvent = event.isRecurringEvent && event.frequency === RecurringFrequency.DAILY;
  const isWeeklyRecurringEvent =
    event.isRecurringEvent && event.frequency === RecurringFrequency.WEEKLY && event.recurringDays;

  if (isDailyRecurringEvent || isWeeklyRecurringEvent) {
    const recurrenceEndDateMs = event.recurrenceEndDateMs;
    const recurrenceString = isDailyRecurringEvent
      ? getDailyRecurrenceString(recurrenceEndDateMs)
      : // @ts-expect-error we're validating recurring days in isWeeklyRecurringEvent
        getWeeklyRecurrenceString(event.recurringDays, recurrenceEndDateMs);
    return (
      <React.Fragment>
        <Paragraph marginY="sm" fontType="bodySmallHeading" data-testid="AvailabilityEventPreviewPopoverBody-Repeat">
          Repeat
        </Paragraph>
        <Paragraph marginY="sm">{recurrenceString}</Paragraph>
      </React.Fragment>
    );
  }

  return null;
};

type AvailabilityEventPreviewPopoverBodyProps = {
  event: AvailabilityEvent;
  onDeleteClick: () => void;
  onEditClick: () => void;
};

const AvailabilityEventPreviewPopoverBody = ({
  event,
  onDeleteClick,
  onEditClick,
}: AvailabilityEventPreviewPopoverBodyProps) => {
  const [displayConfirmationModal, setDisplayConfirmationModal] = useState(false);
  const timeRange = useMemo(() => getTourEventTimeRange(event), [event]);

  const handleDeleteClick = () => {
    if (event.isRecurringEvent) {
      return setDisplayConfirmationModal(true);
    }

    onDeleteClick();
  };

  return (
    <>
      <div data-testid="AvailabilityEventPreviewPopoverBody">
        <Paragraph fontType="bodySmallHeading" data-testid="AvailabilityEventPreviewPopoverBody-Date">
          Date
        </Paragraph>
        <Paragraph marginY="sm">{format(new Date(event.start), CALENDAR_EVENT_DATE_DISPLAY_FORMAT_STRING)}</Paragraph>
        <Paragraph marginY="sm" fontType="bodySmallHeading" data-testid="AvailabilityEventPreviewPopoverBody-Time">
          Time
        </Paragraph>
        <Paragraph marginY="sm">{timeRange}</Paragraph>
        {getRecurringDaysSection(event)}
        <Divider marginY="sm" />
        <Flex as={ButtonGroup} display="flex" justifyContent="flex-end" aria-label="Availability event form buttons">
          <TextButton onClick={handleDeleteClick} data-testid="AvailabilityEventPreviewPopoverBody-DeleteBtn">
            Delete
          </TextButton>
          <Button buttonType="primary" onClick={onEditClick} data-testid="AvailabilityEventPreviewPopoverBody-EditBtn">
            Edit
          </Button>
        </Flex>
      </div>

      {displayConfirmationModal && (
        <RecuringAvailabilityConfirmationModal
          isForDelete={true}
          onClose={() => setDisplayConfirmationModal(false)}
          onConfirm={onDeleteClick}
        />
      )}
    </>
  );
};

export default AvailabilityEventPreviewPopoverBody;
